import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import CustomBreadcrumb from "../../common/CustomBreadcrumb";
import { BreadCrum } from "../../common";
import Loader from "../Loder/loader";
import { getItem } from "../../common/localStorage";
import { getProductParentRoute } from "./productNavigation";
import {
  getClaimDetailAction,
  getClaimDetailHandler,
} from "../../redux/action/getClaimDetailSlice";
import ClaimFormViewOnly from "./ClaimFormViewOnly";
import ClaimQuizViewOnly from "./ClaimQuizViewOnly";
import ClaimQuizSetViewOnly from "./ClaimQuizSetViewOnly";
import ClaimUrlViewOnly from "./ClaimUrlViewOnly";
import {
  formatClaimStatus,
  getClaimTypeLabel,
  getQuizPagination,
  getQuizSetQuestions,
  getQuizSetQuestionsTotalCount,
  getSelectedQuizSetDetail,
  normalizeClaimDetail,
} from "./claimViewUtils";
import { useAdminPermissions } from "../../hooks/useAdminPermissions";

const FORM_PAGE_SIZE = 10;
const QUIZ_SET_PAGE_SIZE = 5;

const ViewClaim = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { state } = useLocation();
  const [formPage, setFormPage] = useState(1);
  const [quizSetPage, setQuizSetPage] = useState(1);
  const [quizSetPageSize, setQuizSetPageSize] = useState(QUIZ_SET_PAGE_SIZE);
  const [selectedSet, setSelectedSet] = useState(null);
  const { hasProductViewOnly, canEditProducts, isPending: permissionsPending } =
    useAdminPermissions();

  const getClaimDetail = useSelector((store) => store?.getClaimDetail);
  
  const claimDetail = useMemo(
    () => normalizeClaimDetail(getClaimDetail?.data),
    [getClaimDetail?.data],
  );

  const productId = Number(getItem("productId")) || Number(state?.productId);
  const refFeatureId = Number(getItem("refFeatureId"));
  const claimId = state?.id;
  const claimType = state?.claimType || claimDetail?.claimType;
  const canAccessPage = hasProductViewOnly;

  useEffect(() => {
    return () => {
      dispatch(getClaimDetailAction.getClaimDetailInfoReset());
    };
  }, [dispatch]);

  useEffect(() => {
    if (permissionsPending || !canEditProducts) {
      return;
    }

    navigate("/claim", { state: state?.fromClaim, replace: true });
  }, [canEditProducts, navigate, permissionsPending, state?.fromClaim]);

  useEffect(() => {
    if (permissionsPending || !canAccessPage) {
      return;
    }

    if (!productId || !refFeatureId || !claimId) {
      return;
    }

    const isQuizSetDetail = claimType === "quiz" && selectedSet;
    const payload = {
      productId,
      refFeatureId,
      claimId,
      formPage: isQuizSetDetail ? formPage : 1,
      formPageSize: FORM_PAGE_SIZE,
      quizSetPage: isQuizSetDetail ? selectedSet.setNumber : quizSetPage,
      quizSetPageSize: isQuizSetDetail ? 1 : quizSetPageSize,
      ...(isQuizSetDetail && selectedSet?.id ? { quizSetId: selectedSet.id } : {}),
    };

    dispatch(getClaimDetailHandler(payload));
  }, [
    canAccessPage,
    claimId,
    claimType,
    dispatch,
    formPage,
    permissionsPending,
    productId,
    quizSetPage,
    quizSetPageSize,
    refFeatureId,
    selectedSet,
  ]);

  useEffect(() => {
    if (permissionsPending || canAccessPage) {
      return;
    }

    navigate("/", { replace: true });
  }, [canAccessPage, navigate, permissionsPending]);

  const handleViewQuizSet = useCallback((setRow) => {
    setSelectedSet(setRow);
    setFormPage(1);
    window.scrollTo(0, 0);
  }, []);

  const handleQuizSetPageChange = useCallback((page, pageSize) => {
    setQuizSetPage(page);
    setQuizSetPageSize(pageSize);
    window.scrollTo(0, 0);
  }, []);

  const handleBackToQuizList = useCallback(() => {
    setSelectedSet(null);
    setFormPage(1);
    window.scrollTo(0, 0);
  }, []);

  if (permissionsPending || canEditProducts || !canAccessPage) {
    return <Loader loaderTransForm="loaderTransForm" />;
  }

  const claimTypeLabel = getClaimTypeLabel(claimType);
  const formattedClaimDetail = {
    ...claimDetail,
    claimName: claimDetail?.claimName || state?.claimName,
    claimType: claimType,
    language: claimDetail?.language || state?.language,
    claimStatus:
      formatClaimStatus(claimDetail?.claimStatus) ||
      state?.claimStatus ||
      "-",
    urlData: claimDetail?.urlData ?? state?.urlData ?? "",
  };

  const totalFormQuestions =
    claimDetail?.formTotalCount ??
    claimDetail?.totalFormQuestions ??
    claimDetail?.formData?.length ??
    0;
  const showFormPagination = totalFormQuestions > FORM_PAGE_SIZE;

  const quizSetQuestions = getQuizSetQuestions(claimDetail, selectedSet);
  const totalQuizSetQuestions = getQuizSetQuestionsTotalCount(claimDetail);
  const showQuizSetQuestionPagination =
    selectedSet && totalQuizSetQuestions > FORM_PAGE_SIZE;
  const selectedSetDetail = getSelectedQuizSetDetail(claimDetail, selectedSet);

  const breadcrumbItems = [
    { text: BreadCrum.HOME },
    {
      text: BreadCrum.PRODUCTS,
      onClick: () => {
        navigate("/product");
        localStorage.removeItem("productId");
      },
    },
    {
      text: BreadCrum.MANAGE_PRODUCTS,
      onClick: () =>
        navigate(getProductParentRoute(state?.fromClaim || {}), {
          state: state?.fromClaim?.productData,
        }),
    },
    {
      text: BreadCrum.CLAIM_LIST,
      onClick: () => navigate("/claim", { state: state?.fromClaim }),
    },
    {
      text: claimTypeLabel,
      active: !selectedSet,
      onClick: selectedSet ? handleBackToQuizList : undefined,
    },
  ];

  if (selectedSet) {
    breadcrumbItems.push({
      text: selectedSetDetail.setNumberLabel,
      active: true,
    });
  }

  return (
    <>
      {getClaimDetail?.isLoading && (
        <Loader loaderTransForm="loaderTransForm" />
      )}
      <div className="rightside">
        <div className="rightcontent">
          <p>
            <strong>{claimTypeLabel}</strong>
          </p>
          <CustomBreadcrumb items={breadcrumbItems} />

          {getClaimDetail?.error ? (
            <div className="sectionpadding bg-white mb-30">
              <p className="release-document-view-empty mb-0">
                {getClaimDetail.error}
              </p>
            </div>
          ) : claimType === "form" ? (
            <>
              <ClaimFormViewOnly claimDetail={formattedClaimDetail} />
              {showFormPagination && (
                <div className="d-flex justify-content-between align-items-center mb-30">
                  <button
                    type="button"
                    className="primarybtn"
                    disabled={formPage <= 1 || getClaimDetail?.isLoading}
                    onClick={() => setFormPage((page) => Math.max(page - 1, 1))}
                  >
                    Previous
                  </button>
                  <span>
                    Page {formPage} of {Math.ceil(totalFormQuestions / FORM_PAGE_SIZE)}
                  </span>
                  <button
                    type="button"
                    className="primarybtn"
                    disabled={
                      formPage >= Math.ceil(totalFormQuestions / FORM_PAGE_SIZE) ||
                      getClaimDetail?.isLoading
                    }
                    onClick={() => setFormPage((page) => page + 1)}
                  >
                    Next
                  </button>
                </div>
              )}
            </>
          ) : claimType === "quiz" ? (
            <>
              {selectedSet ? (
                <ClaimQuizSetViewOnly
                  setDetail={selectedSetDetail}
                  questions={quizSetQuestions}
                />
              ) : (
                <ClaimQuizViewOnly
                  claimDetail={formattedClaimDetail}
                  onViewSet={handleViewQuizSet}
                  quizPagination={getQuizPagination(claimDetail)}
                  isLoading={getClaimDetail?.isLoading}
                  onQuizSetPageChange={handleQuizSetPageChange}
                />
              )}
              {showQuizSetQuestionPagination && (
                <div className="d-flex justify-content-between align-items-center mb-30">
                  <button
                    type="button"
                    className="primarybtn"
                    disabled={formPage <= 1 || getClaimDetail?.isLoading}
                    onClick={() => setFormPage((page) => Math.max(page - 1, 1))}
                  >
                    Previous
                  </button>
                  <span>
                    Page {formPage} of{" "}
                    {Math.ceil(totalQuizSetQuestions / FORM_PAGE_SIZE)}
                  </span>
                  <button
                    type="button"
                    className="primarybtn"
                    disabled={
                      formPage >=
                        Math.ceil(totalQuizSetQuestions / FORM_PAGE_SIZE) ||
                      getClaimDetail?.isLoading
                    }
                    onClick={() => setFormPage((page) => page + 1)}
                  >
                    Next
                  </button>
                </div>
              )}
            </>
          ) : claimType === "url" ? (
            <ClaimUrlViewOnly claimDetail={formattedClaimDetail} />
          ) : (
            <div className="sectionpadding bg-white mb-30">
              <p className="release-document-view-empty mb-0">
                View is not available for this claim type yet.
              </p>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default ViewClaim;
